import { create } from "@bufbuild/protobuf";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    keepPreviousData,
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import type { GitpodAPI } from "@/api";
import { useCursorPagination } from "@/hooks/use-cursor-pagination";
import { useEventInterest } from "@/hooks/use-event-interest";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useResourcePermission } from "@/hooks/use-resource-permission";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { useOrganization } from "@/queries/organization-queries";
import { autoPaginate, DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
import { CountRequestSchema, type CountResponse } from "gitpod-next-api/gitpod/v1/count_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { SortOrder, PaginationRequestSchema, SortSchema } from "gitpod-next-api/gitpod/v1/pagination_pb";
import {
    type ListWorkflowExecutionOutputsResponse_ActionOutput,
    type WorkflowAction,
    type WorkflowExecution,
    type WorkflowExecutionAction,
    WorkflowExecutionActionPhase,
    type WorkflowStep,
    type WorkflowTrigger,
    type WorkflowTriggerContext,
    type Workflow,
    WorkflowExecutionPhase,
    ListWorkflowExecutionActionsRequestSchema,
    WorkflowTriggerSchema,
    WorkflowTrigger_ManualSchema,
    WorkflowTriggerContextSchema,
    WorkflowTriggerContext_ProjectsSchema,
    WorkflowStepSchema,
    CreateWorkflowRequestSchema,
    WorkflowActionSchema,
    WorkflowAction_LimitsSchema,
    UpdateWorkflowRequestSchema,
    DeleteWorkflowRequestSchema,
    StartWorkflowRequestSchema,
    CancelWorkflowExecutionRequestSchema,
    WorkflowExecutionSchema,
    CancelWorkflowExecutionActionRequestSchema,
    WorkflowExecutionActionSchema,
    ListWorkflowExecutionOutputsRequestSchema,
    type WorkflowExecution_Status,
    type WorkflowExecutionAction_Status,
    ListWorkflowsRequest_SortSchema,
    type ListWorkflowsRequest_SortField,
} from "gitpod-next-api/gitpod/v1/workflow_pb";

export type PlainWorkflow = Workflow;
export type PlainWorkflowTrigger = WorkflowTrigger;
export type PlainWorkflowAction = WorkflowAction;
export type PlainWorkflowStep = WorkflowStep;
export type PlainWorkflowExecution = WorkflowExecution;
export type PlainWorkflowExecutionAction = WorkflowExecutionAction;

export function toPlainWorkflow(workflow: Workflow): PlainWorkflow {
    return workflow;
}

export const workflowQueryKey = {
    // Automations
    automations: {
        get: (workflowId?: string) => keyWithPrincipal(["automations", { workflowId }]),
        list: (filter?: WorkflowsFilter, token?: string) =>
            keyWithPrincipal(["automations", "list", { filter: filter ?? {}, token }]),
        cursorPage: (filter?: WorkflowsFilter, token?: string, sortField?: number) =>
            keyWithPrincipal(["automations", "cursor", { filter: filter ?? {}, token, sortField }]),
    },
    webhookSecret: (workflowId?: string) => keyWithPrincipal(["automations", "webhook-secret", { workflowId }]),
    // Workflow Executions
    executions: {
        get: (workflowExecutionId?: string) => keyWithPrincipal(["workflow-executions", { workflowExecutionId }]),
        list: (workflowId: string, filter?: WorkflowExecutionsFilter, page?: number) =>
            keyWithPrincipal(["workflow-executions", "list", workflowId, { filter, page }]),
        cursorPage: (workflowId: string, filter?: WorkflowExecutionsFilter, token?: string, pageSize?: number) =>
            keyWithPrincipal(["workflow-executions", "cursor", workflowId, { filter, token, pageSize }]),
        mostRecent: (workflowId: string) => keyWithPrincipal(["workflow-executions", "most-recent", workflowId]),
    },
    // Workflow Execution Actions
    executionActions: (workflowExecutionId: string) =>
        keyWithPrincipal(["workflow-execution-actions", "list", { workflowExecutionId }]),
    executionAction: (workflowExecutionActionId?: string) =>
        keyWithPrincipal(["workflow-execution-actions", { workflowExecutionActionId }]),
    executionOutputs: (workflowExecutionId: string) =>
        keyWithPrincipal(["workflow-execution-outputs", { workflowExecutionId }]),
};

// Helper to get the list/cursor key prefixes for invalidation (slice off the filter/page/token params)
const automationsListKeyPrefix = () => workflowQueryKey.automations.list().slice(0, -1);
const automationsCursorKeyPrefix = () => workflowQueryKey.automations.cursorPage().slice(0, -1);
const automationsAllKeyPrefix = () => keyWithPrincipal(["automations", "all"]);
const executionsListKeyPrefix = (workflowId: string) => workflowQueryKey.executions.list(workflowId).slice(0, -1);
const executionsCursorKeyPrefix = (workflowId: string) =>
    workflowQueryKey.executions.cursorPage(workflowId).slice(0, -1);
const executionSummaryKeyPrefix = () => keyWithPrincipal(["workflowExecutionSummary"]);
const recentCompletedExecutionsKeyPrefix = () => keyWithPrincipal(["recentCompletedExecutions"]);

export const handleWorkflowEvents = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    switch (evt.resourceType) {
        case ResourceType.WORKFLOW: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const workflow = await refetchWorkflow(api, evt.resourceId);
                setWorkflowInCache(client, evt.resourceId, workflow ? toPlainWorkflow(workflow) : undefined);
                await client.invalidateQueries({ queryKey: automationsListKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsCursorKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsAllKeyPrefix() });
            }
            if (evt.operation === ResourceOperation.CREATE) {
                await client.invalidateQueries({ queryKey: automationsListKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsCursorKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsAllKeyPrefix() });
                await client.invalidateQueries({ queryKey: executionSummaryKeyPrefix() });
            }
            if (evt.operation === ResourceOperation.DELETE) {
                setWorkflowInCache(client, evt.resourceId, undefined);
                await client.invalidateQueries({ queryKey: automationsListKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsCursorKeyPrefix() });
                await client.invalidateQueries({ queryKey: automationsAllKeyPrefix() });
                await client.invalidateQueries({ queryKey: executionSummaryKeyPrefix() });
            }
            break;
        }
        case ResourceType.WORKFLOW_EXECUTION: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                // Get workflowId from cached execution before invalidating
                const cachedExecution = client.getQueryData<PlainWorkflowExecution>(
                    workflowQueryKey.executions.get(evt.resourceId),
                );
                const workflowId = cachedExecution?.metadata?.workflowId;

                // Invalidate individual execution cache
                await client.invalidateQueries({
                    queryKey: workflowQueryKey.executions.get(evt.resourceId),
                });

                // Invalidate list and cursor queries (status changes may affect filtered views)
                if (workflowId) {
                    await client.invalidateQueries({
                        queryKey: executionsListKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: executionsCursorKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executions.mostRecent(workflowId),
                    });
                }

                // Invalidate summary and recent executions so stats cards update
                await client.invalidateQueries({ queryKey: executionSummaryKeyPrefix() });
                await client.invalidateQueries({ queryKey: recentCompletedExecutionsKeyPrefix() });
            }
            if (evt.operation === ResourceOperation.CREATE) {
                // We need to fetch the execution so we can get the workflow ID
                const execution = await refetchWorkflowExecution(api, evt.resourceId);
                if (!execution) {
                    return;
                }

                // Add it to the cache
                const plainExecution = execution;
                setWorkflowExecutionInCache(client, {
                    workflowExecutionId: plainExecution.id,
                    workflowExecution: plainExecution,
                });

                // Invalidate the list and cursor queries for the workflow
                const workflowId = execution?.metadata?.workflowId;
                if (!execution || !workflowId) {
                    return;
                }
                await client.invalidateQueries({
                    queryKey: executionsListKeyPrefix(workflowId),
                });
                await client.invalidateQueries({
                    queryKey: executionsCursorKeyPrefix(workflowId),
                });
                await client.invalidateQueries({
                    queryKey: workflowQueryKey.executions.mostRecent(workflowId),
                });
            }
            if (evt.operation === ResourceOperation.DELETE) {
                const cachedExecution = client.getQueryData<PlainWorkflowExecution>(
                    workflowQueryKey.executions.get(evt.resourceId),
                );
                const workflowId = cachedExecution?.metadata?.workflowId;
                if (!cachedExecution || !workflowId) {
                    return;
                }

                // Remove it from the cache
                setWorkflowExecutionInCache(client, {
                    workflowExecutionId: cachedExecution.id,
                    workflowExecution: undefined,
                });

                // Invalidate the lists and cursor queries
                if (workflowId) {
                    await client.invalidateQueries({
                        queryKey: executionsListKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: executionsCursorKeyPrefix(workflowId),
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executions.mostRecent(workflowId),
                    });
                }

                // Invalidate summary and recent executions so stats cards update
                await client.invalidateQueries({ queryKey: executionSummaryKeyPrefix() });
                await client.invalidateQueries({ queryKey: recentCompletedExecutionsKeyPrefix() });
            }
            break;
        }
        case ResourceType.WORKFLOW_EXECUTION_ACTION: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const action = await refetchWorkflowExecutionAction(api, evt.resourceId);
                if (action?.metadata?.workflowExecutionId) {
                    setWorkflowExecutionActionInCache(client, {
                        workflowExecutionId: action.metadata.workflowExecutionId,
                        workflowExecutionActionId: evt.resourceId,
                        action: action ? action : undefined,
                    });
                }
            }
            if (evt.operation === ResourceOperation.CREATE) {
                const action = await refetchWorkflowExecutionAction(api, evt.resourceId);
                if (action?.metadata?.workflowExecutionId) {
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executionActions(action?.metadata?.workflowExecutionId),
                    });
                }
            }
            if (evt.operation === ResourceOperation.DELETE) {
                const cachedAction = client.getQueryData<PlainWorkflowExecutionAction>(
                    workflowQueryKey.executionAction(evt.resourceId),
                );
                if (cachedAction?.metadata?.workflowExecutionId) {
                    setWorkflowExecutionActionInCache(client, {
                        workflowExecutionId: cachedAction?.metadata?.workflowExecutionId,
                        workflowExecutionActionId: evt.resourceId,
                        action: undefined,
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executionActions(cachedAction?.metadata?.workflowExecutionId),
                    });
                }
            }
            break;
        }
    }
};

const refetchWorkflow = async (api: GitpodAPI, workflowId: string) => {
    try {
        const response = await api.workflowService.getWorkflow({ workflowId });
        return response.workflow;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow", workflowId);
        }
    }
};

const refetchWorkflowExecution = async (
    api: GitpodAPI,
    workflowExecutionId: string,
): Promise<WorkflowExecution | undefined> => {
    try {
        const response = await api.workflowService.getWorkflowExecution({ workflowExecutionId });
        return response.workflowExecution;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow execution", workflowExecutionId);
        }
    }
};

const refetchWorkflowExecutionAction = async (api: GitpodAPI, workflowExecutionActionId: string) => {
    try {
        const response = await api.workflowService.getWorkflowExecutionAction({ workflowExecutionActionId });
        return response.workflowExecutionAction;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow execution action", workflowExecutionActionId);
        }
    }
};

export const useGetWorkflowOptions = (workflowId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: workflowQueryKey.automations.get(workflowId),
        queryFn: async () => {
            if (!workflowId) {
                throw new Error("No workflowId provided");
            }

            const { workflow } = await api.workflowService.getWorkflow({
                workflowId,
            });

            if (!workflow) {
                throw new Error("Error fetching workflow");
            }

            return toPlainWorkflow(workflow);
        },
        enabled: !!workflowId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetWorkflow = (workflowId?: string) => {
    return useQuery(useGetWorkflowOptions(workflowId));
};

export type WorkflowsFilter = {
    search?: string;
    creatorIds?: string[];
    statusPhases?: WorkflowExecutionPhase[];
    hasFailedExecutionSince?: Date;
};

export type UsePaginatedWorkflowsParams = {
    filter?: WorkflowsFilter;
    page?: number;
    enabled?: boolean;
};

type WorkflowListPageData = {
    workflowIds: string[];
    totalCount?: number;
    pageSize: number;
    nextToken?: string;
};

type WorkflowListPageOptionsParams = {
    filter?: WorkflowsFilter;
    token?: string;
    enabled?: boolean;
    queryClient: QueryClient;
};

const useGetWorkflowListPageOptions = ({
    filter,
    token = "",
    enabled = true,
    queryClient,
}: WorkflowListPageOptionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const queryKey = workflowQueryKey.automations.list(filter, token);
    return queryOptions({
        queryKey,
        queryFn: async (): Promise<WorkflowListPageData> => {
            const response = await api.workflowService.listWorkflows({
                filter: {
                    search: filter?.search,
                    creatorIds: filter?.creatorIds,
                    statusPhases: filter?.statusPhases,
                    hasFailedExecutionSince: filter?.hasFailedExecutionSince
                        ? timestampFromDate(filter.hasFailedExecutionSince)
                        : undefined,
                },
                pagination: create(PaginationRequestSchema, { pageSize: DEFAULT_PAGE_SIZE, token }),
            });

            const workflows = response.workflows.map(toPlainWorkflow);
            const pageSize = DEFAULT_PAGE_SIZE;

            // Seed individual workflow caches with the full objects
            for (const workflow of workflows) {
                setWorkflowInCache(queryClient, workflow.id, workflow);
            }

            let totalCount: number | undefined;

            if (!response.pagination?.nextToken && !token) {
                // No nextToken on the first page means all data fits on one page
                totalCount = workflows.length;
            }

            // Return only IDs - full objects are in individual caches
            return {
                workflowIds: workflows.map((w) => w.id),
                totalCount,
                pageSize,
                nextToken: response.pagination?.nextToken,
            };
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

const useWorkflowListPage = ({
    filter,
    token,
    enabled = true,
}: {
    filter?: WorkflowsFilter;
    token?: string;
    enabled?: boolean;
}) => {
    const queryClient = useQueryClient();
    const options = useGetWorkflowListPageOptions({ filter, token, enabled, queryClient });
    return useQuery(options);
};

/**
 * Hook for page-based pagination of workflows.
 * Returns workflowIds, totalCount, and pageSize for use with AdaptivePagination.
 */
export const usePaginatedWorkflows = ({ filter, enabled = true }: Omit<UsePaginatedWorkflowsParams, "page">) => {
    const queryClient = useQueryClient();
    useEventInterest({ resourceType: ResourceType.WORKFLOW });

    // Fetch the first page
    const pageQuery = useWorkflowListPage({ filter, enabled });

    const nextToken = pageQuery.data?.nextToken;

    const nextPageOptions = useGetWorkflowListPageOptions({
        filter,
        token: nextToken,
        enabled: enabled && !!nextToken,
        queryClient,
    });

    // Prefetch the next page using the cursor token from the current response
    useEffect(() => {
        if (nextToken) {
            void queryClient.prefetchQuery(nextPageOptions).catch(console.error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, nextToken, filter, queryClient]);

    const workflowIds = useMemo(() => {
        return pageQuery.data?.workflowIds ?? [];
    }, [pageQuery.data]);

    return {
        ...pageQuery,
        workflowIds,
        totalCount: pageQuery.data?.totalCount,
        pageSize: pageQuery.data?.pageSize ?? DEFAULT_PAGE_SIZE,
        nextToken,
    };
};

type UseWorkflowsCursorPageParams = {
    filter?: WorkflowsFilter;
    enabled?: boolean;
    sortField?: ListWorkflowsRequest_SortField;
};

type WorkflowsCursorPageData = {
    workflowIds: string[];
    nextToken: string;
    count?: CountResponse;
};

export const useWorkflowsCursorPageOptions = ({
    filter,
    enabled = true,
    queryClient,
    token,
    sortField,
}: UseWorkflowsCursorPageParams & { queryClient: QueryClient; token: string }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: workflowQueryKey.automations.cursorPage(filter, token, sortField),
        queryFn: async (): Promise<WorkflowsCursorPageData> => {
            const response = await api.workflowService.listWorkflows({
                filter: {
                    search: filter?.search,
                    creatorIds: filter?.creatorIds,
                    statusPhases: filter?.statusPhases,
                    hasFailedExecutionSince: filter?.hasFailedExecutionSince
                        ? timestampFromDate(filter.hasFailedExecutionSince)
                        : undefined,
                },
                pagination: create(PaginationRequestSchema, {
                    pageSize: DEFAULT_PAGE_SIZE,
                    token,
                }),
                sort: sortField ? create(ListWorkflowsRequest_SortSchema, { field: sortField }) : undefined,
                count: !token ? create(CountRequestSchema, { include: true }) : undefined,
            });

            const workflows = response.workflows.map(toPlainWorkflow);

            // Seed individual workflow caches
            for (const workflow of workflows) {
                setWorkflowInCache(queryClient, workflow.id, workflow);
            }

            return {
                workflowIds: workflows.map((w) => w.id),
                nextToken: response.pagination?.nextToken ?? "",
                count: response.count,
            };
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

/**
 * Fetches a single page of workflows using an opaque cursor token.
 * Manages cursor state internally and resets when filters or sort change.
 */
export const useWorkflowsCursorPage = ({ filter, enabled = true, sortField }: UseWorkflowsCursorPageParams) => {
    const queryClient = useQueryClient();
    const cursor = useCursorPagination();

    // Reset cursor when filters or sort change
    const stableKey = JSON.stringify({ filter, sortField });
    const prevKeyRef = useRef(stableKey);
    useEffect(() => {
        if (prevKeyRef.current !== stableKey) {
            prevKeyRef.current = stableKey;
            cursor.reset();
        }
    }, [stableKey, cursor]);

    const options = useWorkflowsCursorPageOptions({
        filter,
        enabled,
        queryClient,
        token: cursor.currentToken,
        sortField,
    });
    const query = useQuery(options);

    // Prefetch the next page so we know its item count before navigating
    const nextToken = query.data?.nextToken;
    const nextPageOptions = useWorkflowsCursorPageOptions({
        filter,
        enabled: enabled && !!nextToken,
        queryClient,
        token: nextToken ?? "",
        sortField,
    });
    useEffect(() => {
        if (nextToken) {
            void queryClient.prefetchQuery(nextPageOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextToken, queryClient]);

    // Read the prefetched next page's item count from the cache
    const nextPageData = nextToken
        ? queryClient.getQueryData<WorkflowsCursorPageData>(
              workflowQueryKey.automations.cursorPage(filter, nextToken, sortField),
          )
        : undefined;

    return { ...query, cursor, nextPageItemCount: nextPageData?.workflowIds.length };
};

/**
 * Returns a bounded count of workflows matching the given filter without
 * fetching full workflow objects. Uses pageSize=1 with CountRequest.
 */
export const useWorkflowCount = ({ filter, enabled = true }: { filter?: WorkflowsFilter; enabled?: boolean }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: keyWithPrincipal(["automations", "count", { filter: filter ?? {} }]),
        queryFn: async () => {
            const response = await api.workflowService.listWorkflows({
                filter: {
                    search: filter?.search,
                    creatorIds: filter?.creatorIds,
                    statusPhases: filter?.statusPhases,
                    hasFailedExecutionSince: filter?.hasFailedExecutionSince
                        ? timestampFromDate(filter.hasFailedExecutionSince)
                        : undefined,
                },
                pagination: create(PaginationRequestSchema, { pageSize: 1, token: "" }),
                count: create(CountRequestSchema, { include: true }),
            });
            return response.count?.value;
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
    });
};

export type WorkflowExecutionsFilter = {
    statusPhases?: WorkflowExecutionPhase[];
    hasFailedActions?: boolean;
    search?: string;
};

export type UseGetMostRecentExecutionParams = {
    enabled: boolean;
    workflowId: string;
};

export const useGetMostRecentExecutionIdOptions = ({
    enabled,
    workflowId,
    queryClient,
}: UseGetMostRecentExecutionParams & { queryClient: QueryClient }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: workflowQueryKey.executions.mostRecent(workflowId),
        queryFn: async (): Promise<string | null> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: [
                        WorkflowExecutionPhase.PENDING,
                        WorkflowExecutionPhase.RUNNING,
                        WorkflowExecutionPhase.STOPPING,
                        WorkflowExecutionPhase.STOPPED,
                        WorkflowExecutionPhase.COMPLETED,
                    ],
                },
                sort: create(SortSchema, {
                    field: "startedAt",
                    order: SortOrder.DESC,
                }),
                pagination: create(PaginationRequestSchema, { pageSize: 1 }),
            });

            if (response.workflowExecutions.length === 0) {
                return null;
            }

            const execution = response.workflowExecutions[0];

            // Seed individual execution cache
            setWorkflowExecutionInCache(queryClient, {
                workflowExecutionId: execution.id,
                workflowExecution: execution,
            });

            return execution.id;
        },
        enabled: enabled && !!user && !!workflowId,
        throwOnError: false,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useGetMostRecentExecutionId = ({ enabled, workflowId }: UseGetMostRecentExecutionParams) => {
    const queryClient = useQueryClient();
    return useQuery(useGetMostRecentExecutionIdOptions({ enabled, workflowId, queryClient }));
};

export type WorkflowExecutionListPageData = {
    executionIds: string[];
    totalCount?: number;
    totalPages?: number;
    pageSize: number;
    nextToken?: string;
};

export type UsePaginatedWorkflowExecutionsParams = {
    enabled: boolean;
    workflowId: string;
    filter?: WorkflowExecutionsFilter;
    page?: number;
};

type WorkflowExecutionListPageOptionsParams = UsePaginatedWorkflowExecutionsParams & {
    queryClient: QueryClient;
};

const useGetWorkflowExecutionListPageOptions = ({
    workflowId,
    filter,
    page = 0,
    enabled = true,
    queryClient,
}: WorkflowExecutionListPageOptionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const queryKey = workflowQueryKey.executions.list(workflowId, filter, page);
    return queryOptions({
        queryKey,
        queryFn: async (): Promise<WorkflowExecutionListPageData> => {
            const token = page === 0 ? "" : String(page);
            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: filter?.statusPhases,
                    hasFailedActions: filter?.hasFailedActions,
                    search: filter?.search,
                },
                pagination: create(PaginationRequestSchema, { pageSize: DEFAULT_PAGE_SIZE, token }),
            });

            const executions = response.workflowExecutions.map((execution) => execution);
            const pageSize = DEFAULT_PAGE_SIZE;

            // Seed individual execution caches with the full objects
            for (const execution of executions) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowExecutionId: execution.id,
                    workflowExecution: execution,
                });
            }

            // Extract metadata from pagination token (format: "pageNumber/totalPages/totalCount")
            let totalPages: number | undefined;
            let totalCount: number | undefined;

            if (response.pagination?.nextToken) {
                const parts = response.pagination.nextToken.split("/");
                if (parts.length === 3) {
                    totalPages = parseInt(parts[1], 10);
                    totalCount = parseInt(parts[2], 10);
                }
            } else if (page === 0) {
                // No nextToken on page 0 means all data fits on one page
                totalPages = 1;
                totalCount = executions.length;
            }

            // Return only IDs - full objects are in individual caches
            return {
                executionIds: executions.map((e) => e.id),
                totalCount,
                totalPages,
                pageSize,
                nextToken: response.pagination?.nextToken,
            };
        },
        enabled: enabled && !!user && !!workflowId,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

/**
 * Simple hook that fetches a single page of workflow executions.
 */
const useWorkflowExecutionListPage = ({
    workflowId,
    filter,
    page = 0,
    enabled = true,
}: UsePaginatedWorkflowExecutionsParams) => {
    const queryClient = useQueryClient();
    const options = useGetWorkflowExecutionListPageOptions({ workflowId, filter, page, enabled, queryClient });
    return useQuery(options);
};

/**
 * Hook for page-based pagination of workflow executions.
 * Returns executionIds, totalCount, totalPages, and pageSize for use with AdaptivePagination.
 */
export const usePaginatedWorkflowExecutions = ({
    workflowId,
    filter,
    page = 0,
    enabled = true,
}: UsePaginatedWorkflowExecutionsParams) => {
    const queryClient = useQueryClient();
    useEventInterest({ resourceType: ResourceType.WORKFLOW_EXECUTION });

    // Fetch the requested page
    const pageQuery = useWorkflowExecutionListPage({ workflowId, filter, page, enabled });

    // Fetch page 0 for metadata (React Query deduplicates if page === 0)
    const page0Query = useWorkflowExecutionListPage({ workflowId, filter, page: 0, enabled });

    // Use metadata from page 0, fall back to current page
    const metadata = {
        totalCount: page0Query.data?.totalCount ?? pageQuery.data?.totalCount,
        totalPages: page0Query.data?.totalPages ?? pageQuery.data?.totalPages ?? 0,
        pageSize: page0Query.data?.pageSize ?? DEFAULT_PAGE_SIZE,
        nextToken: pageQuery.data?.nextToken,
    };

    const nextPageOptions = useGetWorkflowExecutionListPageOptions({
        workflowId,
        filter,
        page: page + 1,
        enabled,
        queryClient,
    });

    // Prefetch the next page
    useEffect(() => {
        void queryClient.prefetchQuery(nextPageOptions).catch(console.error);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, page, workflowId, filter, queryClient]);

    const executionIds = useMemo(() => {
        return pageQuery.data?.executionIds ?? [];
    }, [pageQuery.data]);

    return {
        ...pageQuery,
        executionIds,
        ...metadata,
    };
};

type UseWorkflowExecutionsCursorPageParams = {
    enabled: boolean;
    workflowId: string;
    filter?: WorkflowExecutionsFilter;
    pageSize?: number;
};

type WorkflowExecutionsCursorPageData = {
    executionIds: string[];
    nextToken: string;
};

export const useWorkflowExecutionsCursorPageOptions = ({
    workflowId,
    filter,
    enabled = true,
    pageSize,
    queryClient,
    token,
}: UseWorkflowExecutionsCursorPageParams & { queryClient: QueryClient; token: string }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: workflowQueryKey.executions.cursorPage(workflowId, filter, token, pageSize),
        queryFn: async (): Promise<WorkflowExecutionsCursorPageData> => {
            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: filter?.statusPhases,
                    hasFailedActions: filter?.hasFailedActions,
                    search: filter?.search,
                },
                pagination: create(PaginationRequestSchema, {
                    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
                    token,
                }),
            });

            const executions = response.workflowExecutions.map((execution) => execution);

            // Seed individual execution caches
            for (const execution of executions) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowExecutionId: execution.id,
                    workflowExecution: execution,
                });
            }

            return {
                executionIds: executions.map((e) => e.id),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        enabled: enabled && !!user && !!workflowId,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

/**
 * Fetches a single page of workflow executions using an opaque cursor token.
 * Manages cursor state internally and resets when filters change.
 */
export const useWorkflowExecutionsCursorPage = ({
    workflowId,
    filter,
    enabled = true,
    pageSize,
}: UseWorkflowExecutionsCursorPageParams) => {
    const queryClient = useQueryClient();
    const cursor = useCursorPagination();

    // Reset cursor when filters change
    const filterKey = JSON.stringify(filter);
    const prevFilterKeyRef = useRef(filterKey);
    useEffect(() => {
        if (prevFilterKeyRef.current !== filterKey) {
            prevFilterKeyRef.current = filterKey;
            cursor.reset();
        }
    }, [filterKey, cursor]);

    const options = useWorkflowExecutionsCursorPageOptions({
        workflowId,
        filter,
        enabled,
        pageSize,
        queryClient,
        token: cursor.currentToken,
    });
    const query = useQuery(options);

    return { ...query, cursor };
};

export type UseListWorkflowExecutionActionsParams = {
    enabled: boolean;
    workflowExecutionId: string;
};

export const useListWorkflowExecutionActionsOptions = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionActionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: workflowQueryKey.executionActions(workflowExecutionId),
        queryFn: async (): Promise<PlainWorkflowExecutionAction[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = create(ListWorkflowExecutionActionsRequestSchema, {
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });
            const workflowExecutionActions = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionActions(params),
                request,
                (response) => response.workflowExecutionActions,
            );
            return workflowExecutionActions.map((action) => action);
        },
        enabled: enabled && !!user,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useListWorkflowExecutionActions = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionActionsParams) => {
    useEventInterest({ resourceType: ResourceType.WORKFLOW_EXECUTION_ACTION });
    return useQuery(useListWorkflowExecutionActionsOptions({ enabled, workflowExecutionId }));
};

export const useGetWorkflowExecutionOptions = (workflowExecutionId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: workflowQueryKey.executions.get(workflowExecutionId),
        queryFn: async () => {
            if (!workflowExecutionId) {
                throw new Error("No workflowExecutionId provided");
            }

            const { workflowExecution } = await api.workflowService.getWorkflowExecution({
                workflowExecutionId,
            });

            if (!workflowExecution) {
                throw new Error("Error fetching workflow execution");
            }

            return workflowExecution;
        },
        enabled: !!workflowExecutionId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetWorkflowExecution = (workflowExecutionId?: string) => {
    return useQuery(useGetWorkflowExecutionOptions(workflowExecutionId));
};

export const useGetWorkflowExecutionActionOptions = (workflowExecutionActionId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: workflowQueryKey.executionAction(workflowExecutionActionId),
        queryFn: async () => {
            if (!workflowExecutionActionId) {
                throw new Error("No workflowExecutionActionId provided");
            }

            const { workflowExecutionAction } = await api.workflowService.getWorkflowExecutionAction({
                workflowExecutionActionId,
            });

            if (!workflowExecutionAction) {
                throw new Error("Error fetching workflow execution action");
            }

            return workflowExecutionAction;
        },
        enabled: !!workflowExecutionActionId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetWorkflowExecutionAction = (workflowExecutionActionId?: string) => {
    return useQuery(useGetWorkflowExecutionActionOptions(workflowExecutionActionId));
};

export type CreateWorkflowParams = {
    name?: string;
    description?: string;
    executor?: Subject;
    triggers?: PlainWorkflowTrigger[];
    steps?: PlainWorkflowStep[];
    limits?: {
        maxParallel?: number;
        maxTotal?: number;
    };
};

export const useCreateWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateWorkflowParams) => {
            const defaultTriggers = [
                create(WorkflowTriggerSchema, {
                    trigger: {
                        case: "manual",
                        value: create(WorkflowTrigger_ManualSchema),
                    },
                    context: create(WorkflowTriggerContextSchema, {
                        context: {
                            case: "projects",
                            value: create(WorkflowTriggerContext_ProjectsSchema, {
                                projectIds: [],
                            }),
                        },
                    }),
                }),
            ];

            const defaultSteps = [
                create(WorkflowStepSchema, {
                    step: {
                        case: "agent",
                        value: {
                            prompt: "With prompts you can send messages to Ona Agent",
                        },
                    },
                }),
                create(WorkflowStepSchema, {
                    step: {
                        case: "task",
                        value: {
                            command: "echo 'with commands you can run shell commands'",
                        },
                    },
                }),
            ];

            const request = create(CreateWorkflowRequestSchema, {
                name: params.name,
                description: params.description || "",
                triggers: params.triggers || defaultTriggers,
                executor: params.executor,
                action: create(WorkflowActionSchema, {
                    limits: create(WorkflowAction_LimitsSchema, {
                        maxParallel: params.limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                        maxTotal: params.limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                    }),
                    steps: params.steps || defaultSteps,
                }),
            });
            const response = await api.workflowService.createWorkflow(request);
            if (!response.workflow) {
                throw new Error("Error creating workflow");
            }
            return response.workflow;
        },
        onSuccess: (workflow) => {
            if (workflow) {
                const plainWorkflow = toPlainWorkflow(workflow);
                setWorkflowInCache(queryClient, workflow.id, plainWorkflow);
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UpdateWorkflowParams = {
    workflowId: string;
    name?: string;
    description?: string;
    executor?: Subject;
    action?: PlainWorkflowAction;
    triggers?: PlainWorkflowTrigger[];
    disabled?: boolean;
};

export const useUpdateWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            workflowId,
            name,
            description,
            executor,
            action,
            triggers,
            disabled,
        }: UpdateWorkflowParams) => {
            const request = create(UpdateWorkflowRequestSchema, {
                workflowId,
                name,
                description,
                executor,
                action,
                triggers,
                disabled,
            });
            const response = await api.workflowService.updateWorkflow(request);
            if (!response.workflow) {
                throw new Error("Failed to update workflow");
            }
            return toPlainWorkflow(response.workflow);
        },
        onSuccess: (updatedWorkflow) => {
            setWorkflowInCache(queryClient, updatedWorkflow.id, updatedWorkflow);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, force = false }: { workflowId: string; force?: boolean }) => {
            const request = create(DeleteWorkflowRequestSchema, { workflowId, force });
            return await api.workflowService.deleteWorkflow(request);
        },
        onSuccess: (_, { workflowId }) => {
            setWorkflowInCache(queryClient, workflowId, undefined);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type StartWorkflowParams = {
    workflowId: string;
    contextOverride?: WorkflowTriggerContext;
};

export const useStartWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, contextOverride }: StartWorkflowParams) => {
            const request = create(StartWorkflowRequestSchema, {
                workflowId,
                contextOverride: contextOverride ? create(WorkflowTriggerContextSchema, contextOverride) : undefined,
            });
            const response = await api.workflowService.startWorkflow(request);
            if (!response.workflowExecution) {
                throw new Error("Failed to start workflow");
            }
            return response.workflowExecution;
        },
        onSuccess: (execution) => {
            setWorkflowExecutionInCache(queryClient, {
                workflowExecutionId: execution.id,
                workflowExecution: execution,
            });

            const workflowId = execution.metadata?.workflowId;
            if (workflowId) {
                // Update the most-recent query so the AutomationCard immediately
                // shows the new running execution instead of "No executions yet".
                queryClient.setQueryData(workflowQueryKey.executions.mostRecent(workflowId), execution.id);

                // Invalidate execution lists so paginated views pick up the new entry.
                void queryClient.invalidateQueries({ queryKey: executionsListKeyPrefix(workflowId) });
                void queryClient.invalidateQueries({ queryKey: executionsCursorKeyPrefix(workflowId) });
                void queryClient.invalidateQueries({ queryKey: executionSummaryKeyPrefix() });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelWorkflowExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (execution: PlainWorkflowExecution) => {
            const request = create(CancelWorkflowExecutionRequestSchema, { workflowExecutionId: execution.id });
            await api.workflowService.cancelWorkflowExecution(request);
            return execution.id;
        },
        onSuccess: (_executionId: string, execution: PlainWorkflowExecution) => {
            if (execution.metadata?.workflowId) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowExecutionId: execution.id,
                    workflowExecution: create(WorkflowExecutionSchema, {
                        ...execution,
                        status: {
                            ...execution.status,
                            phase: WorkflowExecutionPhase.STOPPING,
                        } as WorkflowExecution_Status,
                    }),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelWorkflowExecutionAction = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (action: PlainWorkflowExecutionAction) => {
            const request = create(CancelWorkflowExecutionActionRequestSchema, {
                workflowExecutionActionId: action.id,
            });
            await api.workflowService.cancelWorkflowExecutionAction(request);
            return action.id;
        },
        onSuccess: (_actionId: string, action: PlainWorkflowExecutionAction) => {
            if (action.metadata?.workflowExecutionId) {
                setWorkflowExecutionActionInCache(queryClient, {
                    workflowExecutionId: action.metadata.workflowExecutionId,
                    workflowExecutionActionId: action.id,
                    action: create(WorkflowExecutionActionSchema, {
                        ...action,
                        status: {
                            ...action.status,
                            phase: WorkflowExecutionActionPhase.STOPPING,
                        } as WorkflowExecutionAction_Status,
                    }),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export function setWorkflowInCache(client: QueryClient, workflowId: string, workflow?: PlainWorkflow) {
    client.setQueryData(workflowQueryKey.automations.get(workflowId), workflow ? workflow : null);
}

export function setWorkflowExecutionInCache(
    client: QueryClient,
    data: {
        workflowExecutionId: string;
        workflowExecution?: PlainWorkflowExecution;
    },
) {
    const { workflowExecutionId, workflowExecution } = data;
    client.setQueryData(
        workflowQueryKey.executions.get(workflowExecutionId),
        workflowExecution ? workflowExecution : null,
    );
}

export function setWorkflowExecutionActionInCache(
    client: QueryClient,
    data: {
        workflowExecutionId: string;
        workflowExecutionActionId: string;
        action?: PlainWorkflowExecutionAction;
    },
) {
    const { workflowExecutionId, workflowExecutionActionId, action } = data;
    client.setQueryData(workflowQueryKey.executionAction(workflowExecutionActionId), action ? action : null);
    client.setQueryData(
        workflowQueryKey.executionActions(workflowExecutionId),
        (currentData?: PlainWorkflowExecutionAction[]) => {
            if (!currentData) {
                return action ? [action] : currentData;
            }

            const updatedList = currentData
                .map((a: PlainWorkflowExecutionAction) => {
                    if (a.id !== workflowExecutionActionId) {
                        return a;
                    }
                    return action;
                })
                .filter((a) => !!a);

            // Add to the top of the list if it doesn't already exist
            if (action && !currentData.some((a) => a.id === workflowExecutionActionId)) {
                updatedList.unshift(action);
            }

            return updatedList;
        },
    );
}

export const useGetWorkflowWebhookSecretOptions = (workflowId?: string) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: workflowQueryKey.webhookSecret(workflowId),
        queryFn: async () => {
            if (!workflowId) {
                throw new Error("No workflowId provided");
            }

            const response = await api.workflowService.getWorkflowWebhookSecret({
                workflowId,
            });

            if (!response.secret) {
                throw new Error("Error fetching webhook secret");
            }

            return response.secret;
        },
        enabled: !!workflowId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetWorkflowWebhookSecret = (workflowId?: string) => {
    return useQuery(useGetWorkflowWebhookSecretOptions(workflowId));
};

export const useRotateWorkflowWebhookSecret = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (workflowId: string) => {
            const response = await api.workflowService.rotateWorkflowWebhookSecret({
                workflowId,
            });

            if (!response.secret) {
                throw new Error("Failed to rotate webhook secret");
            }

            return response.secret;
        },
        onSuccess: (newSecret, workflowId) => {
            queryClient.setQueryData(workflowQueryKey.webhookSecret(workflowId), newSecret);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type PlainWorkflowExecutionActionOutput = ListWorkflowExecutionOutputsResponse_ActionOutput;

export type UseListWorkflowExecutionOutputsParams = {
    enabled: boolean;
    workflowExecutionId: string;
};

export const useListWorkflowExecutionOutputsOptions = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionOutputsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: workflowQueryKey.executionOutputs(workflowExecutionId),
        queryFn: async (): Promise<PlainWorkflowExecutionActionOutput[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = create(ListWorkflowExecutionOutputsRequestSchema, {
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });
            const outputs = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionOutputs(params),
                request,
                (response) => response.outputs,
            );
            return outputs.map((output) => output);
        },
        enabled: enabled && !!user,
        throwOnError: false,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useListWorkflowExecutionOutputs = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionOutputsParams) => {
    return useQuery(useListWorkflowExecutionOutputsOptions({ enabled, workflowExecutionId }));
};

export const useCanCreateAutomation = () => {
    const { data: organization } = useOrganization();
    const { hasPermission: canCreateAutomations, isLoading } = useResourcePermission(
        ResourceType.ORGANIZATION,
        organization?.id || "",
        "workflow:create",
    );
    return {
        canCreateAutomations,
        isLoading,
    };
};

export const useCanEditAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canEditAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflow:update",
    );
    return {
        canEditAutomation: workflowId ? canEditAutomation : false,
        isLoading,
    };
};

export const useCanDeleteAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canDeleteAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflow:delete",
    );
    return {
        canDeleteAutomation: workflowId ? canDeleteAutomation : false,
        isLoading,
    };
};

export const useCanShareAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canShareAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflow:grant",
    );
    return {
        canShareAutomation: workflowId ? canShareAutomation : false,
        isLoading,
    };
};

export const useCanRunAutomation = (workflowId: string | undefined) => {
    const { hasPermission: canRunAutomation, isLoading } = useResourcePermission(
        ResourceType.WORKFLOW,
        workflowId || "",
        "workflowexecution:create",
    );
    return {
        canRunAutomation: workflowId ? canRunAutomation : false,
        isLoading,
    };
};

export const useRecentCompletedExecutionsOptions = ({
    workflowIds,
    pageSize = 100,
    enabled = true,
    stableKey,
}: {
    workflowIds: string[];
    pageSize?: number;
    enabled?: boolean;
    stableKey: string;
}) => {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: keyWithPrincipal(["recentCompletedExecutions", stableKey, pageSize]),
        queryFn: async (): Promise<PlainWorkflowExecution[]> => {
            // API allows max 25 workflow IDs per request — fan out in parallel
            const batches: string[][] = [];
            for (let i = 0; i < workflowIds.length; i += 25) {
                batches.push(workflowIds.slice(i, i + 25));
            }
            const results = await Promise.all(
                batches.map((batch) =>
                    api.workflowService.listWorkflowExecutions({
                        filter: {
                            workflowIds: batch,
                            statusPhases: [WorkflowExecutionPhase.COMPLETED, WorkflowExecutionPhase.STOPPED],
                        },
                        sort: create(SortSchema, { field: "finishedAt", order: SortOrder.DESC }),
                        pagination: create(PaginationRequestSchema, { pageSize }),
                    }),
                ),
            );
            return results.flatMap((r) => r.workflowExecutions);
        },
        enabled: enabled && workflowIds.length > 0,
        staleTime: 2 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
    });
};

/**
 * Fetches recent completed executions for time-bucketed visualizations (e.g. run history bars).
 * Returns up to `pageSize` most-recently-finished executions.
 */
export const useRecentCompletedExecutions = ({
    workflowIds,
    pageSize = 100,
    enabled = true,
}: {
    workflowIds: string[];
    pageSize?: number;
    enabled?: boolean;
}) => {
    const stableKey = useMemo(() => [...workflowIds].sort().join(","), [workflowIds]);
    return useQuery(useRecentCompletedExecutionsOptions({ workflowIds, pageSize, enabled, stableKey }));
};

export const useWorkflowExecutionSummaryOptions = ({
    startTime,
    endTime,
    workflowIds,
    enabled = true,
    stableWfKey,
}: {
    startTime: Date;
    endTime: Date;
    workflowIds?: string[];
    enabled?: boolean;
    stableWfKey: string;
}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: keyWithPrincipal([
            "workflowExecutionSummary",
            stableWfKey,
            { startTime: startTime.toISOString(), endTime: endTime.toISOString() },
        ]),
        queryFn: async () => {
            const response = await api.workflowService.getWorkflowExecutionSummary({
                filter: {
                    dateRange: {
                        startTime: timestampFromDate(startTime),
                        endTime: timestampFromDate(endTime),
                    },
                    workflowIds: workflowIds ?? [],
                },
            });
            return {
                successfulCount: Number(response.successfulCount),
                failedCount: Number(response.failedCount),
                totalCount: Number(response.totalCount),
                totalWorkflows: Number(response.totalWorkflowsInOrganization),
            };
        },
        enabled: enabled && !!user,
        staleTime: 2 * 60 * 1000,
        throwOnError: false,
        retry: defaultRetry,
    });
};

/**
 * Fetches aggregate execution counts for the given date range using the
 * GetWorkflowExecutionSummary RPC.
 */
export const useWorkflowExecutionSummary = ({
    startTime,
    endTime,
    workflowIds,
    enabled = true,
}: {
    startTime: Date;
    endTime: Date;
    workflowIds?: string[];
    enabled?: boolean;
}) => {
    const stableWfKey = useMemo(() => (workflowIds ? [...workflowIds].sort().join(",") : ""), [workflowIds]);
    return useQuery(useWorkflowExecutionSummaryOptions({ startTime, endTime, workflowIds, enabled, stableWfKey }));
};
